import React from 'react';

import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';

import AccountTypes from './AccountTypes';
import Layout, { StyledCheckbox } from './Layout';
import LinkSent from './LinkSent';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { MissingBeneficiary } from '../../services/api/types/co_pilot/missing_beneficiaries';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const MissingBeneficiaryRow: React.FC<React.PropsWithChildren<{ missingBeneficiary: MissingBeneficiary }>> = ({
  missingBeneficiary: { id, clientName, accountTypes, linkSentAt, queuedAt },
}) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<MissingBeneficiary>();

  return (
    <tr>
      <td>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          {clientName}
        </Box>
      </td>
      <AccountTypes accountTypes={accountTypes} />
      <LinkSent linkSentAt={linkSentAt} queuedAt={queuedAt} />
    </tr>
  );
};

const MissingBeneficiaryTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<MissingBeneficiary>();
  return (
    <table>
      <thead>
        <tr>
          <th style={{ padding: token('spacing.space.200') }}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <StyledCheckbox
                name="selectAll"
                id="selectAll"
                role="checkbox"
                type="checkbox"
                className="checkboxInputBase"
                checked={status === 'allSelected'}
                onChange={() => handleSelectAllChange()}
              />
              <Text css={{ textStyle: 'title.100' }}>Client</Text>
            </Box>
          </th>
          <th style={{ padding: token('spacing.space.200') }}>
            <Text css={{ textStyle: 'title.100' }}>Accounts</Text>
          </th>
          <th style={{ padding: token('spacing.space.200') }}>
            <Text css={{ textStyle: 'title.100' }}>Link sent</Text>
          </th>
        </tr>
      </thead>
      <tbody>
        {itemList?.map(missingBeneficiary => (
          <MissingBeneficiaryRow key={missingBeneficiary.id} missingBeneficiary={missingBeneficiary} />
        ))}
        {!lastPage && (
          <tr>
            <td>
              <Box css={{ px: 'space.200', pb: 'space.300' }}>
                <Button variant="textPrimary" onClick={() => loadMore()}>
                  Load more
                </Button>
              </Box>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const MissingBeneficiaries: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<MissingBeneficiary[], CoPilotResponseMeta>(`/api/v1/co_pilot/missing_beneficiaries`, {
    skip: true,
  });
  const [postSendReminder] = useMutation<{}, ActionRequestParams>(`/api/v1/co_pilot/missing_beneficiaries/reminders`);
  const [postDismissal] = useMutation<{}, ActionRequestParams>('/api/v1/co_pilot/missing_beneficiaries/dismissals');

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
      postSendReminder={postSendReminder}
    >
      <Layout
        activeTab="Missing beneficiaries"
        header="These clients have an account without a beneficiary."
        subHeader="Remind your clients to designate beneficiaries via email below. We’ll make sure to copy you on the email."
      >
        <MissingBeneficiaryTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default MissingBeneficiaries;
